import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { PrimaryButton, SecondaryButton } from "./DesignSystemButtons";
import { fetchTable } from "hooks/useTableQuery";

export function DeviceSelect({ endpoint, value, onChange }) {
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [pages, setPages] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = (value || "").split(",").filter(Boolean).map(code => options.find(o => o.value === code) || { value: code, label: code === "none" ? "No device" : code });
  const available = [...options, ...selected.filter(item => !options.some(option => option.value === item.value))];
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const data = await fetchTable(`${endpoint}?${new URLSearchParams({ search: term, page })}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!Array.isArray(data.results)) throw new Error("Could not load device options.");
        setOptions([{ value: "none", label: "No device" }, ...data.results]);
        setPages(data.pagination?.total_pages ?? (data.next ? page + 1 : page)); setError("");
      } catch (e) { if (!controller.signal.aborted) setError(e.message); }
      finally { if (!controller.signal.aborted) setBusy(false); }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [endpoint, term, page]);
  return <Mui.Stack spacing={1}>
    <Mui.Autocomplete multiple options={available} filterOptions={items => items} loading={busy}
      value={selected}
      isOptionEqualToValue={(a, b) => a.value === b.value} getOptionLabel={o => o.label || o.value}
      onInputChange={(_, text, reason) => { if (reason === "input" || reason === "clear") { setTerm(text); setPage(1); } }}
      onChange={(_, items) => onChange(items.map(o => o.value).join(","))}
      renderInput={params => <Mui.TextField {...params} label="Device" size="small" />} />
    {error && <Mui.Alert severity="error">{error}</Mui.Alert>}
    {pages > 1 && <Mui.Pagination size="small" page={page} count={pages} onChange={(_, next) => setPage(next)} />}
  </Mui.Stack>;
}

export function StandardListControls({ table, config, title, actions }) {
  const cardField = config.cardField || "status";
  const dateField = config.defaultDateField || "event_time";
  const [draft, setDraft] = useState({});
  const open = () => { setDraft({ ...table.state }); table.setDrawerOpen(true); };
  const change = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const remove = name => table.setParams({ [name]: null });
  const filterKeys = config.filters.flatMap(f => f.type === "date" ? [`${f.key}_from`, `${f.key}_to`] : [f.key]);
  const clear = () => table.setParams(Object.fromEntries([...filterKeys, config.quickFilter?.key, "search", "sort_by", "sort_order", dateField].filter(Boolean).map(k => [k, null])));
  const defaultDate = table.data?.query?.defaults_applied?.includes(dateField);
  const activeFilters = config.filters.filter(field => field.type === "date" ? table.state[`${field.key}_from`] || table.state[`${field.key}_to`] : table.state[field.key]).length;
  const footer = <Mui.Stack direction="row" spacing={1}>
    <SecondaryButton onClick={() => table.setDrawerOpen(false)}>Cancel</SecondaryButton>
    <PrimaryButton onClick={() => {
      const changes = Object.fromEntries([...filterKeys, dateField].map(key => [key, draft[key] || null]));
      table.setParams(changes); table.setDrawerOpen(false);
    }}>Apply</PrimaryButton>
  </Mui.Stack>;
  return <Mui.Stack spacing={1.5} sx={{ p: 2 }}>
    <Mui.Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {config.cards.map(card => <Mui.Button key={card.value} variant={(table.state[cardField] || "ALL") === card.value ? "contained" : "outlined"}
        onClick={() => table.setParams({ [cardField]: card.value === "ALL" || table.state[cardField] === card.value ? null : card.value })}>
        {card.label}{table.data?.counts ? ` (${table.data.counts[card.value] ?? 0})` : ""}
      </Mui.Button>)}
    </Mui.Stack>
    <Mui.Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {title}
      <Mui.TextField size="small" label={config.searchLabel || "Search"} value={table.search} inputProps={{ maxLength: 100 }}
        onFocus={() => table.setSearchFocused(true)} onBlur={() => table.setSearchFocused(false)} onChange={e => table.setSearch(e.target.value)} />
      {config.quickFilter && <Mui.TextField select size="small" label={config.quickFilter.label} value={table.state[config.quickFilter.key] || ""} sx={{ minWidth: 110 }} onChange={e => table.setParams({ [config.quickFilter.key]: e.target.value })}>
        {["", ...config.quickFilter.values].map(v => <Mui.MenuItem key={v} value={v}>{v || "All"}</Mui.MenuItem>)}
      </Mui.TextField>}
      {config.deviceEndpoint && <Mui.Box sx={{ minWidth: 220, flex: "0 1 260px" }}><DeviceSelect endpoint={config.deviceEndpoint} value={table.state.device} onChange={value => table.setParams({ device: value })} /></Mui.Box>}
      {config.filters.filter(field => field.type === "date" && field.quick).map(field => ["from", "to"].map(bound => <Mui.TextField
        key={`${field.key}_${bound}`} type="date" size="small" label={`${field.label} ${bound}`} InputLabelProps={{ shrink: true }} value={table.state[`${field.key}_${bound}`] || ""}
        onChange={e => table.setParams({ [`${field.key}_${bound}`]: e.target.value, [field.key]: null })} />))}
      <SecondaryButton onClick={open}>Filters{activeFilters ? ` (${activeFilters})` : ""}</SecondaryButton>
      {actions}
      <SecondaryButton onClick={table.refresh} disabled={table.inFlight}>Refresh</SecondaryButton>
    </Mui.Stack>
    <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {defaultDate && !table.state[`${dateField}_from`] && !table.state[`${dateField}_to`] && table.state[dateField] !== "all" && <Mui.Chip label={config.defaultDateLabel || "Date: Last 7 days (default)"} onDelete={() => table.setParams({ [dateField]: "all" })} />}
      {Object.entries(table.state).filter(([k]) => !["page", "page_size"].includes(k)).map(([key, value]) => <Mui.Chip key={key} label={`${config.labels[key] || key}: ${value}`} onDelete={() => remove(key)} />)}
      <Mui.Button size="small" onClick={clear}>Clear all</Mui.Button>
    </Mui.Stack>
    <GlobalSidePanel open={table.drawerOpen} onClose={() => table.setDrawerOpen(false)} title="Filters" footer={footer}>
      <Mui.Stack spacing={2} sx={{ p: 2 }}>
        {config.filters.map(field => field.type === "date" ? <Mui.Stack key={field.key} spacing={1}>
          <Mui.Typography>{field.label}</Mui.Typography>
          {["from", "to"].map(bound => <Mui.TextField key={bound} type="date" label={bound === "from" ? "From" : "To"} InputLabelProps={{ shrink: true }} value={draft[`${field.key}_${bound}`] || ""}
            onChange={e => { change(`${field.key}_${bound}`, e.target.value); if (field.key === dateField) change(dateField, ""); }} />)}
        </Mui.Stack> : field.type === "device" ? <DeviceSelect key={field.key} endpoint={config.deviceEndpoint} value={draft[field.key]} onChange={value => change(field.key, value)} /> :
          <Mui.TextField key={field.key} select label={field.label} value={draft[field.key]?.split(",").filter(Boolean) || []} SelectProps={{ multiple: true }}
            onChange={e => change(field.key, e.target.value.join(","))}>
            {field.values.map(value => <Mui.MenuItem key={value} value={value}>{value}</Mui.MenuItem>)}
          </Mui.TextField>)}
      </Mui.Stack>
    </GlobalSidePanel>
  </Mui.Stack>;
}

export function ListRefreshIndicator({ table, sx }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const age = table.updatedAt === null ? null : Math.max(0, Math.floor((now - table.updatedAt) / 1000));
  return <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, ...sx }}>
    <Mui.Typography variant="caption" aria-live="off">{age === null ? "Awaiting update" : `Updated ${age}s ago`}{table.blocked ? " · Paused" : ""}</Mui.Typography>
    <Mui.Button size="small" onClick={() => table.setPaused(value => !value)}>{table.paused ? "Resume" : "Pause"}</Mui.Button>
  </Mui.Stack>;
}
